import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState
} from 'react';
import {useLocation} from 'react-router';
import {useIsMobileView} from '../lib/useMediaQuery';
import {isEmbeddedInExtension} from '../lib/extensionEmbed';
import VoiceAssistantControl from './VoiceAssistantControl';

const storageKey = 'voice-assistant-dock-top-ratio';
const userPositionedStorageKey = 'voice-assistant-dock-user-positioned';
const layoutModeStorageKey = 'voice-assistant-dock-layout-mode';
const compactDefaultTopRatio = 0.12;
const wideAssistantActionsDefaultTopRatio = 0.75;
// Inside the Chrome extension side panel the viewport is phone-narrow but
// desktop-tall, with the composer pinned to the bottom and the app header at
// the top: park the tab on the right edge at mid height, over the message
// list, where it covers no controls.
const embeddedExtensionDefaultTopRatio = 0.5;
const minTopRatio = 0;
const maxTopRatio = 0.88;
const dragThresholdPx = 4;
const assistantFloatingActionsBreakpointPx = 1440;
const desktopBreakpointPx = 992;

const clampTopRatio = (
    ratio: number,
    fallback = compactDefaultTopRatio,
    minimum = minTopRatio
) =>
    Number.isFinite(ratio)
        ? Math.min(maxTopRatio, Math.max(minimum, ratio))
        : fallback;

type VoiceDockLayoutMode =
    | 'mobile'
    | 'mobile-assistant-thread'
    | 'compact-desktop'
    | 'wide-desktop'
    | 'wide-assistant-thread'
    | 'embedded-extension';

const getVoiceDockLayoutMode = (
    windowWidth: number,
    pathname: string
): VoiceDockLayoutMode => {
    if (isEmbeddedInExtension()) {
        return 'embedded-extension';
    }
    if (windowWidth < desktopBreakpointPx) {
        return isAssistantThreadPath(pathname)
            ? 'mobile-assistant-thread'
            : 'mobile';
    }
    if (windowWidth <= assistantFloatingActionsBreakpointPx) {
        return 'compact-desktop';
    }
    if (isAssistantThreadPath(pathname)) return 'wide-assistant-thread';

    return 'wide-desktop';
};

const readStoredTopRatio = (layoutMode: VoiceDockLayoutMode): number | null => {
    if (typeof window === 'undefined') return null;

    try {
        if (window.localStorage.getItem(userPositionedStorageKey) !== 'true') {
            return null;
        }

        if (window.localStorage.getItem(layoutModeStorageKey) !== layoutMode) {
            return null;
        }

        const item = window.localStorage.getItem(storageKey);
        if (!item) return null;

        const parsed = JSON.parse(item);
        return typeof parsed === 'number' ? parsed : null;
    } catch (error) {
        console.warn(`Error reading localStorage key "${storageKey}":`, error);
        return null;
    }
};

const writeStoredTopRatio = (
    ratio: number,
    layoutMode: VoiceDockLayoutMode
) => {
    if (typeof window === 'undefined') return;

    try {
        window.localStorage.setItem(storageKey, JSON.stringify(ratio));
        window.localStorage.setItem(userPositionedStorageKey, 'true');
        window.localStorage.setItem(layoutModeStorageKey, layoutMode);
    } catch (error) {
        console.warn(`Error setting localStorage key "${storageKey}":`, error);
    }
};

const clearStoredTopRatio = () => {
    if (typeof window === 'undefined') return;

    try {
        window.localStorage.removeItem(storageKey);
        window.localStorage.removeItem(userPositionedStorageKey);
        window.localStorage.removeItem(layoutModeStorageKey);
    } catch (error) {
        console.warn(`Error clearing localStorage key "${storageKey}":`, error);
    }
};

const getWindowWidth = () =>
    typeof window === 'undefined' ? 0 : window.innerWidth;

const isAssistantThreadPath = (pathname: string) =>
    /^\/assistants\/[^/]+(?:\/threads\/[^/]+)?\/?$/.test(pathname);

const isVisibleTopChromeElement = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    const style = window.getComputedStyle(element);

    return (
        rect.width > 0 &&
        rect.height > 0 &&
        style.display !== 'none' &&
        style.visibility !== 'hidden'
    );
};

const getStableTopChromeBottom = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();

    return rect.bottom > 0 ? rect.bottom : rect.height;
};

const getVisibleNavbarBottom = () => {
    if (typeof document === 'undefined') return 0;

    return Array.from(document.querySelectorAll<HTMLElement>('.navbar')).reduce(
        (bottom, element) => {
            return isVisibleTopChromeElement(element)
                ? Math.max(bottom, getStableTopChromeBottom(element))
                : bottom;
        },
        0
    );
};

const getVisibleAssistantMobileNavigationBottom = () => {
    if (
        typeof document === 'undefined' ||
        getWindowWidth() >= desktopBreakpointPx
    ) {
        return 0;
    }

    return Array.from(
        document.querySelectorAll<HTMLElement>('.assistant-mobile-navigation')
    ).reduce((bottom, element) => {
        return isVisibleTopChromeElement(element)
            ? Math.max(bottom, getStableTopChromeBottom(element))
            : bottom;
    }, 0);
};

const PersistentVoiceAssistantControl: React.FC = () => {
    const location = useLocation();
    // Touch layouts get a tap-to-open bottom sheet instead of the hover
    // popover; the drag logic below keeps its own width constant.
    const isMobileView = useIsMobileView();
    const initialLayoutMode = getVoiceDockLayoutMode(
        getWindowWidth(),
        location.pathname
    );
    const [windowWidth, setWindowWidth] = useState(getWindowWidth);
    const [storedTopRatio, setStoredTopRatio] = useState<number | null>(() =>
        readStoredTopRatio(initialLayoutMode)
    );
    const [topBoundaryRatio, setTopBoundaryRatio] = useState(
        compactDefaultTopRatio
    );
    const [isDragging, setIsDragging] = useState(false);
    const dockRef = useRef<HTMLDivElement | null>(null);
    const layoutModeRef = useRef<VoiceDockLayoutMode>(initialLayoutMode);
    const dragRef = useRef<{
        pointerId: number;
        startY: number;
        startTopRatio: number;
        hasDragged: boolean;
    } | null>(null);
    const suppressNextClickRef = useRef(false);
    const layoutMode = getVoiceDockLayoutMode(windowWidth, location.pathname);
    const useWideAssistantThreadDefault =
        layoutMode === 'wide-assistant-thread';
    const useBottomMobileAssistantThreadDock =
        layoutMode === 'mobile-assistant-thread' &&
        storedTopRatio === null &&
        !isDragging;
    const defaultTopRatio = useWideAssistantThreadDefault
        ? wideAssistantActionsDefaultTopRatio
        : layoutMode === 'embedded-extension'
          ? Math.max(embeddedExtensionDefaultTopRatio, topBoundaryRatio)
          : topBoundaryRatio;
    const dockTopRatio = clampTopRatio(
        storedTopRatio ?? defaultTopRatio,
        defaultTopRatio,
        topBoundaryRatio
    );

    const updateTopBoundaryRatio = useCallback(() => {
        const viewportHeight = Math.max(window.innerHeight, 1);
        const dockHeight = dockRef.current?.getBoundingClientRect().height ?? 0;
        const topBoundaryPx =
            Math.max(
                getVisibleNavbarBottom(),
                getVisibleAssistantMobileNavigationBottom()
            ) +
            dockHeight / 2;
        const nextTopBoundaryRatio = Math.min(
            maxTopRatio,
            Math.max(minTopRatio, topBoundaryPx / viewportHeight)
        );

        setTopBoundaryRatio(previous =>
            previous === nextTopBoundaryRatio ? previous : nextTopBoundaryRatio
        );
    }, []);

    const handleWindowPointerMove = useCallback(
        (event: PointerEvent) => {
            const drag = dragRef.current;
            if (!drag || drag.pointerId !== event.pointerId) {
                return;
            }

            const deltaY = event.clientY - drag.startY;
            if (!drag.hasDragged && Math.abs(deltaY) < dragThresholdPx) {
                return;
            }

            drag.hasDragged = true;
            suppressNextClickRef.current = true;
            setIsDragging(true);
            event.preventDefault();

            const viewportHeight = Math.max(window.innerHeight, 1);
            const nextTopRatio = clampTopRatio(
                drag.startTopRatio + deltaY / viewportHeight,
                compactDefaultTopRatio,
                topBoundaryRatio
            );
            setStoredTopRatio(nextTopRatio);
            writeStoredTopRatio(nextTopRatio, layoutMode);
        },
        [layoutMode, topBoundaryRatio]
    );

    const handleWindowPointerUp = useCallback((event: PointerEvent) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) {
            return;
        }

        dragRef.current = null;
        setIsDragging(false);

        if (drag.hasDragged) {
            window.setTimeout(() => {
                suppressNextClickRef.current = false;
            }, 0);
        }
    }, []);

    useEffect(() => {
        window.addEventListener('pointermove', handleWindowPointerMove);
        window.addEventListener('pointerup', handleWindowPointerUp);
        window.addEventListener('pointercancel', handleWindowPointerUp);

        return () => {
            window.removeEventListener('pointermove', handleWindowPointerMove);
            window.removeEventListener('pointerup', handleWindowPointerUp);
            window.removeEventListener('pointercancel', handleWindowPointerUp);
        };
    }, [handleWindowPointerMove, handleWindowPointerUp]);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(getWindowWidth());
            updateTopBoundaryRatio();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [updateTopBoundaryRatio]);

    useLayoutEffect(() => {
        const timeoutIds = [
            window.setTimeout(updateTopBoundaryRatio, 0),
            window.setTimeout(updateTopBoundaryRatio, 50),
            window.setTimeout(updateTopBoundaryRatio, 250)
        ];
        const animationFrameId = window.requestAnimationFrame(
            updateTopBoundaryRatio
        );

        updateTopBoundaryRatio();

        return () => {
            timeoutIds.forEach(timeoutId => window.clearTimeout(timeoutId));
            window.cancelAnimationFrame(animationFrameId);
        };
    }, [location.pathname, updateTopBoundaryRatio]);

    useEffect(() => {
        if (typeof MutationObserver === 'undefined') {
            return;
        }

        let animationFrameId: number | null = null;
        const scheduleBoundaryUpdate = () => {
            if (animationFrameId !== null) {
                return;
            }

            animationFrameId = window.requestAnimationFrame(() => {
                animationFrameId = null;
                updateTopBoundaryRatio();
            });
        };
        const observer = new MutationObserver(scheduleBoundaryUpdate);

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        return () => {
            observer.disconnect();
            if (animationFrameId !== null) {
                window.cancelAnimationFrame(animationFrameId);
            }
        };
    }, [updateTopBoundaryRatio]);

    useEffect(() => {
        if (layoutModeRef.current === layoutMode) {
            return;
        }

        layoutModeRef.current = layoutMode;
        clearStoredTopRatio();
        setStoredTopRatio(null);
    }, [layoutMode]);

    useEffect(() => {
        setStoredTopRatio(ratio =>
            ratio === null
                ? null
                : clampTopRatio(ratio, compactDefaultTopRatio, topBoundaryRatio)
        );
    }, [topBoundaryRatio]);

    useEffect(() => {
        const handleStorageChange = (event: StorageEvent) => {
            if (
                event.key === storageKey ||
                event.key === userPositionedStorageKey ||
                event.key === layoutModeStorageKey
            ) {
                setStoredTopRatio(readStoredTopRatio(layoutMode));
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [layoutMode]);

    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        if (event.button !== 0) {
            return;
        }

        const rect = event.currentTarget.getBoundingClientRect();
        const viewportHeight = Math.max(window.innerHeight, 1);
        const startTopRatio = clampTopRatio(
            (rect.top + rect.height / 2) / viewportHeight,
            dockTopRatio,
            topBoundaryRatio
        );

        dragRef.current = {
            pointerId: event.pointerId,
            startY: event.clientY,
            startTopRatio,
            hasDragged: false
        };
    };

    const handleClickCapture = (event: React.MouseEvent<HTMLDivElement>) => {
        if (!suppressNextClickRef.current) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
    };

    // Only the position that changes at runtime stays inline; the rest lives
    // in scss/partials/_voice.scss under .voice-assistant-dock.
    const dockStyle: React.CSSProperties = useBottomMobileAssistantThreadDock
        ? {left: '66%'}
        : {top: `${dockTopRatio * 100}%`};
    const dockClassName = [
        'position-fixed',
        'voice-assistant-dock',
        useBottomMobileAssistantThreadDock
            ? 'voice-assistant-dock--bottom-tab'
            : 'voice-assistant-dock--edge-tab',
        isDragging ? 'voice-assistant-dock--dragging' : ''
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div
            ref={dockRef}
            className={dockClassName}
            style={dockStyle}
            onPointerDown={handlePointerDown}
            onClickCapture={handleClickCapture}
        >
            <div className="voice-assistant-dock__shell shadow-sm">
                <VoiceAssistantControl
                    id="voice-assistant-control-persistent"
                    tooltipPlacement="left"
                    presentation={isMobileView ? 'sheet' : 'hover'}
                />
            </div>
        </div>
    );
};

export default PersistentVoiceAssistantControl;
